import { useState } from 'react'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'
import { Link } from 'react-router-dom'

export default function SemulikiValley() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/semuliki-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Semuliki National Park
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            East Africa's Only True Lowland Tropical Rainforest
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Semuliki National Park represents one of Africa's most biologically diverse and geologically fascinating
                protected areas. Established in October 1993, this 220 square kilometer park in Uganda's Bundibugyo
                District constitutes East Africa's only true lowland tropical rainforest—an eastward extension of the
                vast Ituri Forest of the Congo Basin.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                This ancient forest survived the last ice age 12,000-18,000 years ago, creating a living museum of
                prehistoric biological diversity. Semuliki functions as a biological bridge between Central and East
                African ecosystems, hosting 441 bird species (including 35 Guinea-Congo forest species found nowhere
                else in East Africa), 53 mammals, and 8 primate species. The park's famous Sempaya Hot Springs boil at
                over 100 degrees Celsius, adding geological wonder to exceptional biodiversity.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Size</h3>
                  <p className="text-gray-700">220 km²</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Established</h3>
                  <p className="text-gray-700">October 1993</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Bird Species</h3>
                  <p className="text-gray-700">441 species</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Mammal Species</h3>
                  <p className="text-gray-700">53 species</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/semuliki-hero.jpg"
                alt="Semuliki National Park"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Distance:</strong> 320 km to Sempaya Gate</p>
                  <p><strong>Travel Time:</strong> 5-6 hours via Fort Portal route</p>
                  <p><strong>Route:</strong> Kampala → Mubende → Fort Portal → Bundibugyo → Semuliki</p>
                  <p className="text-sm italic">Well-maintained tarmac to Fort Portal, then murram road to park. 4WD recommended for rainy season.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Kasese Airfield:</strong> Charter flights from Entebbe (1 hour). Located 2-3 hours drive from Semuliki.</p>
                  <p><strong>Fort Portal Area:</strong> Nearby airstrips serve western Uganda circuit.</p>
                  <p className="text-sm italic">Charter flights arranged through aviation operators. Combine with Kibale and Rwenzori destinations.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-route" className="w-6 h-6" />
                  From Other Parks
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Fort Portal:</strong> 1.5 hours drive. Perfect base for exploring Semuliki.</p>
                  <p><strong>Kibale NP:</strong> 1.5-2 hours drive. Excellent primate and forest combination.</p>
                  <p><strong>Rwenzori Mountains:</strong> 2-3 hours. Ideal for diverse ecosystem experiences.</p>
                  <p className="text-sm italic">Semuliki fits perfectly into western Uganda circuit itineraries.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-fire" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Sempaya Hot Springs</h3>
                <p className="text-gray-700">
                  Two spectacular geothermal springs boil at over 100°C. The "male" spring (Bitende) measures 12 meters wide while the "female" spring (Nyansimbi) spans 2 meters. Steam erupts dramatically through forest vegetation.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-dove" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">441 Bird Species</h3>
                <p className="text-gray-700">
                  Over 35% of Uganda's total avifauna thrives here. 35 Guinea-Congo forest biome species found nowhere else in East Africa include lyre-tailed honeyguide, Congo serpent eagle, and western bronze-naped pigeon.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-paw" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">8 Primate Species</h3>
                <p className="text-gray-700">
                  Chimpanzees, red-tailed monkeys, black-and-white colobus, Central African red colobus, Dent's Mona monkeys, and nocturnal primates inhabit ancient forest. Less habituated than Kibale but authentic encounters.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-water" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Semuliki River</h3>
                <p className="text-gray-700">
                  This 140km river originates from Lake Edward, flows through the park forming Uganda-DRC border, and empties into Lake Albert. Boat trips reveal hippos, crocodiles, and exceptional wetland birdlife.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-users" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Batwa Cultural Encounters</h3>
                <p className="text-gray-700">
                  Four ethnic groups (Bakonjo, Bwamba, Batuku, Batwa pygmies) live around the park. Batwa forest people share traditional hunting techniques, medicinal plant knowledge, and spiritual forest connections.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-tree" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Ancient Lowland Rainforest</h3>
                <p className="text-gray-700">
                  East Africa's only true lowland tropical rainforest survived the last ice age. Congo Basin extension features towering Uganda ironwood trees creating dense canopy harboring incredible biological diversity.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Lake Albert Views</h3>
                <p className="text-gray-700">
                  Park edges offer spectacular views over Lake Albert and the western arm of the East African Rift Valley. Dramatic escarpments create stunning photographic landscapes combining forest and water.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-globe" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Geological Wonders</h3>
                <p className="text-gray-700">
                  Semuliki sits in the western arm of the Albertine Rift Valley. Geothermal activity manifests in hot springs while ancient forest ecology creates unique habitat supporting species found nowhere else in the region.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-fire" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Sempaya Hot Springs Hike</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Trek to geothermal springs boiling at over 100°C. Watch eggs cook in the steam and learn about the cultural significance of the "male" and "female" springs.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $50 per person</p>
                <Link to="/activities/sempaya-hot-springs" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-paw" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Primate Tracking</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Track chimpanzees and rare primates like De Brazza's monkey through lowland tropical rainforest—East Africa's only Congo Basin ecosystem.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-5 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $90 per person</p>
                <button onClick={() => handleBooking('Primate Tracking - Semuliki')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-moon" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Night Nature Walks</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Spotlighting walks reveal nocturnal wildlife including bushbabies, pottos, and elusive forest predators in this ancient rainforest.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $60 per person</p>
                <Link to="/activities/semuliki-night-walks" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-dove" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Birding Safaris</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Discover 441 bird species including 35 Guinea-Congo endemics found nowhere else in East Africa. A birder's paradise with expert guides.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-6 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy to Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $85 per person</p>
                <button onClick={() => handleBooking('Birding Safaris - Semuliki')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Dry Seasons (December-February, June-September):</strong> Optimal for forest walks and hot springs visits. Less muddy trails, easier forest navigation. Peak birding season.</p>
                  <p><strong>Wet Seasons (March-May, October-November):</strong> Lush forest scenery, excellent primate activity. Trails can be muddy and challenging but rewarding for serious birders seeking migrants.</p>
                  <p className="text-sm italic">Hot springs accessible year-round. 4WD essential during heavy rains.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Waterproof hiking boots, rain jacket, long pants and sleeves, insect repellent, leech socks</p>
                  <p><strong>Recommended:</strong> Daypack, binoculars, field guides, sunscreen, water bottle, camera with telephoto lens</p>
                  <p><strong>Weather Gear:</strong> Expect humidity and potential rain year-round in lowland rainforest</p>
                  <p className="text-sm italic">Neutral earth-tone clothing. Bring spare socks. Trails can be muddy even in dry season.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Vaccinations:</strong> Yellow fever certificate required for entry to Uganda. Typhoid and hepatitis A recommended.</p>
                  <p><strong>Malaria:</strong> High-risk area. Prophylaxis essential. Use insect repellent and sleep under mosquito nets.</p>
                  <p><strong>Fitness:</strong> Moderate fitness required for forest walks. Trails involve uneven terrain and humidity. Pace yourself.</p>
                  <p><strong>Hot Springs:</strong> Do not touch boiling water. Follow guide instructions around geothermal features.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Duration:</strong> 2-3 days ideal for experiencing hot springs, birding, and forest walks. Combine with Kibale or Rwenzori.</p>
                  <p><strong>Accommodation:</strong> Limited options near park. Budget guesthouses in Bundibugyo or Fort Portal. Book ahead.</p>
                  <p><strong>Activities:</strong> Hot springs visit most popular. Birding exceptional value for species diversity.</p>
                  <p><strong>Guides:</strong> Essential for navigation and wildlife spotting. Park rangers provide expertise.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation options are limited near Semuliki. Most visitors stay in Fort Portal or simple lodges near the park entrance.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Accommodation Name</th>
                  <th className="px-6 py-4 text-left">Location</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Semuliki Safari Lodge</td>
                  <td className="px-6 py-4">Near park entrance</td>
                  <td className="px-6 py-4">Luxury tented camp, river views, gourmet dining, exclusive</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Nyati Game Lodge</td>
                  <td className="px-6 py-4">Fort Portal area</td>
                  <td className="px-6 py-4">Comfortable cottages, restaurant, bar, convenient base</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Ntoroko Game Lodge</td>
                  <td className="px-6 py-4">Near Lake Albert</td>
                  <td className="px-6 py-4">Lake views, bandas, restaurant, birding access</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">UWA Bandas</td>
                  <td className="px-6 py-4">Inside park</td>
                  <td className="px-6 py-4">Basic bandas, immediate park access, affordable</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">UWA Campsites</td>
                  <td className="px-6 py-4">Inside park</td>
                  <td className="px-6 py-4">Basic camping facilities, communal amenities, budget-friendly</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Hotel Vanilla (Bundibugyo)</td>
                  <td className="px-6 py-4">Bundibugyo town</td>
                  <td className="px-6 py-4">Simple rooms, local restaurant, basic amenities</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What makes Semuliki unique?</h3>
                <p className="text-gray-700">
                  Semuliki is East Africa's only true lowland tropical rainforest—an extension of the Congo Basin. This creates unique biodiversity including 35 bird species found nowhere else in East Africa. Combined with geothermal hot springs, it offers experiences unavailable elsewhere in the region.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How difficult are the forest walks?</h3>
                <p className="text-gray-700">
                  Moderate difficulty. Trails involve uneven terrain, humidity, and potential mud. Hot springs walk is easiest (2-3 hours round trip). Kirumia Trail more challenging (4-5 hours). Moderate fitness sufficient. Guides adapt pace to group abilities.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I see chimpanzees?</h3>
                <p className="text-gray-700">
                  Chimpanzees inhabit Semuliki but are less habituated than Kibale. Sightings possible but not guaranteed. For reliable chimp tracking, visit Kibale (1.5 hours away). Semuliki excels at birding and unique rainforest experiences.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How long should I spend at Semuliki?</h3>
                <p className="text-gray-700">
                  2-3 days ideal for experiencing hot springs, birding, and forest exploration. Day trips from Fort Portal possible but rushed. Serious birders benefit from longer stays targeting Congo Basin specials. Combines well with Kibale.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is Semuliki family-friendly?</h3>
                <p className="text-gray-700">
                  Moderately family-friendly. Hot springs walk suitable for older children (8+) with reasonable fitness. Humidity and trail conditions challenging for young children. Malaria risk requires extra precautions. Better for adventurous families than first-time safari-goers.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What birding species are special?</h3>
                <p className="text-gray-700">
                  35 Guinea-Congo forest biome species found nowhere else in East Africa. Key targets: lyre-tailed honeyguide, Congo serpent eagle, western bronze-naped pigeon, Nkulengu rail, spot-breasted ibis, black-wattled hornbill. Exceptional for serious birders.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I combine Semuliki with other parks?</h3>
                <p className="text-gray-700">
                  Absolutely! Excellent addition to western Uganda circuits. Combine with Kibale (1.5h), Queen Elizabeth NP (3-4h), or Rwenzori Mountains (2-3h). Fort Portal makes perfect base for exploring multiple destinations in the region.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What about the hot springs safety?</h3>
                <p className="text-gray-700">
                  Hot springs are safe to visit with guides. Water boils at 100°C+—do not touch! Guides demonstrate cooking eggs and plantains. Steam eruptions are natural and spectacular. Follow guide instructions around geothermal features. Photography allowed from safe distances.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Discover East Africa's Hidden Rainforest Paradise
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Journey into Semuliki and experience one of Africa's most biodiverse destinations. From boiling hot springs
            to rare Congo Basin bird species, Semuliki offers an authentic rainforest adventure showcasing the incredible
            diversity of the African continent where ancient forest meets modern conservation.
          </p>
          <Link
            to="/plan-your-trip"
            className="btn-primary text-lg inline-block"
          >
            Plan Your Semuliki Adventure
          </Link>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
